import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router';
import ModuleLink, { navigateToModule } from '@ui/ModuleLink.jsx';
import { stashSignupPrefill } from '@ui/signupHandoff.js';
import { renderFirstPageThumbnail } from '@ui/pdfThumbnail.js';
import FreeAccountBox from '../freeSignup/FreeAccountBox.jsx';
import {
  SIGNUP_SOURCE,
  trackDemoUploadResult,
  trackDemoUploadStarted,
  trackFullDemoStarted,
  trackSampleOrderViewed,
  trackSignupCta
} from '@ui/analytics/events.js';

// Ported from prototypes/landing-try-now.html - the public front door (see
// routes.js, prerendered per react-router.config.js). CTAs point at
// /sign-up and /sign-in instead of the prototype's placeholder
// signup.html/# links.
//
// "Or try a sample order" is still the original fully client-side
// simulation (fixed sample data, typewriter effect) - it always was, and
// stays that way: it's a scripted tour of the UI, not a real scan. Dropping
// or picking a REAL file instead runs it through services/tourguide's
// anonymous demo flow (see CLAUDE.md's tour-guide section and
// docs/tourGuide.md) - a real Gemini extraction, shown with the exact same
// typewriter presentation once it comes back, so the two paths are visually
// indistinguishable except for how the "Workshop Notes" content was
// produced.
const SAMPLE_JOB = {
  concern: 'C/O grinding noise on brake application',
  tech: `C/O grinding noise on brake application. Road tested — confirmed
metal-on-metal contact frt L. Pulled frt wheels, pads worn to
backing plate both sides. Rotors scored, out of spec at 0.5mm [TIR].
Replaced frt pads & rotors w/ [OEM] equivalent. Resurfaced rr
rotors, replaced rr pads (50% remaining, customer requested axle
set). Bled system, topped [DOT4]. Test drove 6km — pedal firm,
no noise, no pull.`,
  cust: `Your front brake pads had worn down completely, so metal was grinding directly against metal every time you braked — that's the noise you were hearing. We replaced the front pads and rotors, refreshed the rear brakes to match, and bled the system with fresh fluid so the pedal feels solid again.`
};
const SAMPLE = { ro: '48213', vehicle: '2019 Toyota Camry', jobCount: 2, job: SAMPLE_JOB };

// The demo's at-rest encryption key (see services/decipher/crypto/
// resultEncryption.js and services/tourguide's DEMO_KEY_COOKIE). Generated
// here, sent once to POST /tour/api/files/getUploadToken, and never read
// back - tourguide stores it as an HttpOnly cookie from that point on, so
// this value only ever needs to be held in this module's own memory for the
// span of one visit, not persisted anywhere the page could read it again.
function generateDemoKey() {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

// Every tour-guide call below throws its own fallback wording on a non-OK
// response, but a request tourguide rejected for a reason a visitor can act
// on (DEMO_KEY_MISSING, DEMO_KEY_INVALID - see server.js) carries its own
// `error` string, meant to be shown as-is rather than replaced by the
// generic fallback.
async function readErrorMessage(res, fallback) {
  try {
    const body = await res.json();
    return body?.error || fallback;
  } catch {
    return fallback;
  }
}

const TYPEWRITER_STEP_CHARS = 3;
const TYPEWRITER_STEP_MS = 8;
const SCAN_DELAY_MS = 1100;
const CUSTOMER_REVEAL_DELAY_MS = 700;
const COPY_FEEDBACK_MS = 1500;
const COMING_SOON_MS = 1800;
const POLL_INTERVAL_MS = 1500;
const MAX_POLL_ATTEMPTS = 60; // ~90s - a real Gemini extraction, not a fixed animation.

// The trial box's three navigable states (see pushHash below) - real
// history entries so the browser's own back/forward can move between them,
// named for what's in view rather than the internal phase/signup-reason
// values that drive them.
const HASH_PREVIEW = '#preview';
const HASH_START_TRIAL = '#start-trial';
const HASH_CREATE_ACCOUNT = '#create-account';

export function meta() {
  return [
    { title: 'WriteUp' },
    {
      name: 'description',
      content: 'Your handwriting expert for the workshop - turn scanned repair orders into clean tech write-ups and plain-language customer summaries in seconds.'
    }
  ];
}

// Route-scoped - only this page uses the handwritten "scribble" treatment
// in the hero, so the font stays out of root.jsx's global link list.
export function links() {
  return [
    {
      rel: 'stylesheet',
      href: 'https://fonts.googleapis.com/css2?family=Reenie+Beanie&display=swap'
    }
  ];
}

export default function Landing() {
  const navigate = useNavigate();
  const location = useLocation();
  const [source, setSource] = useState(null); // 'sample' | 'upload' | null
  const [phase, setPhase] = useState('idle'); // 'idle' | 'scanning' | 'done' | 'error'
  const [result, setResult] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [techText, setTechText] = useState('');
  const [techClamped, setTechClamped] = useState(false);
  const [custRevealed, setCustRevealed] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [comingSoon, setComingSoon] = useState(false);
  const [copiedTech, setCopiedTech] = useState(false);
  const [copiedCust, setCopiedCust] = useState(false);
  const [showFreeAccountBox, setShowFreeAccountBox] = useState(false);
  const [signupReason, setSignupReason] = useState(null); // 'forced' | 'voluntary'
  const [isReprocessing, setIsReprocessing] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  // The front thumbnail's own on-screen box (px), sized to the largest
  // rectangle that (a) fits within 75% of the placeholder and (b) matches
  // the real image's natural aspect ratio - see the effect below for why
  // this can't just be CSS percentages/aspect-ratio on the wrapper.
  const [thumbBoxSize, setThumbBoxSize] = useState(null);

  const fileInputRef = useRef(null);
  const thumbContainerRef = useRef(null);
  const trialBoxRef = useRef(null);
  const techOutRef = useRef(null);
  const comingSoonTimer = useRef(null);
  // Lazily created on the first real upload attempt, then reused for the
  // rest of this visit (a second drop, a "Show More" reprocess) - see
  // generateDemoKey above.
  const demoKeyRef = useRef(null);
  // The real dropped/picked file for the CURRENT upload attempt, held only
  // in memory here so the thumbnail effect below can rasterize its own
  // first page client-side - see that effect's comment for why this
  // replaced a server round trip. Also what startShowMore re-sends for the
  // full-preview upload, since decipher never keeps a copy of an anonymous
  // visitor's document for a reprocess call to reread (see that function).
  const droppedFileRef = useRef(null);
  // FingerprintJS's device id, cached after the first real upload so
  // startShowMore's own upload call doesn't have to load and run it a
  // second time - see getVisitorId below.
  const visitorIdRef = useRef(null);
  // Starts false, which is also what the prerendered html is built with - see
  // the header's own comment for why that's the default rather than a
  // "don't know yet" state.
  const [signedIn, setSignedIn] = useState(false);
  const scanTimers = useRef([]);

  // A background check, not a blocking one - the prerendered page (see
  // react-router.config.js) is already visible instantly, and most visitors
  // to the public landing page are signed out, so gating first paint on this
  // would trade a real perceived-performance win for a check that rarely
  // changes anything. /auth/status is the same {authenticated} shape the old
  // AuthGate.jsx polled - a plain backend call rather than
  // authClient.useSession() specifically so this works the same way
  // whether or not the visitor already has a live Better Auth session, see
  // services/auth/server.js's verifySession.
  //
  // It used to REDIRECT a signed-in visitor straight to /app, and that had to
  // go: {authenticated} is a session check, and a session is not the same
  // thing as a usable account. Someone mid-registration holds a real session
  // for an account with no credential on it, so `/` bounced them to /app,
  // which bounced them to /finish-signup - and every attempt to get back to
  // the front page did it again. Teaching this endpoint to tell the two apart
  // is not cheap (it would need what /auth/verify does; see that route), and
  // the redirect was never worth much anyway: nobody asked to be moved. So
  // the answer now only changes what the header offers, and every route this
  // page links to keeps working for everyone.
  useEffect(() => {
    let cancelled = false;
    fetch('/auth/status')
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`Unexpected status ${res.status}`))))
      .then((data) => {
        if (!cancelled) setSignedIn(!!data.authenticated);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!focusMode) return;
    // The box itself is a fixed, flex-centered dialog while focusMode is on
    // (see the "Interactive demo" section below) - no scrollIntoView needed
    // here, just locking the background page from scrolling behind it.
    // focusMode is only ever true while the SCANNING panel is showing (see
    // exitScanningPanel below) - once a result lands the box drops back
    // into normal flow and re-centers itself explicitly instead.
    document.documentElement.classList.add('overflow-hidden');
    return () => document.documentElement.classList.remove('overflow-hidden');
  }, [focusMode]);

  useEffect(() => {
    // Reveal the "Show more" link only once the clamped notes actually
    // overflow their fixed-height box - mirrors the prototype's
    // scrollHeight/clientHeight check, re-run as the typewriter fills it in.
    if (techOutRef.current) {
      setTechClamped(techOutRef.current.scrollHeight > techOutRef.current.clientHeight);
    }
  }, [techText]);

  useEffect(() => () => scanTimers.current.forEach(clearTimeout), []);

  // The imported-order preview's real first-page thumbnail (see the DONE
  // panel below) - only available for a real upload, never for the
  // simulated sample scan, which has no dropped file to rasterize. Rendered
  // client-side (pdf.js, loaded on demand - see @ui/pdfThumbnail.js) from
  // the dropped File itself rather than asked of decipher: an anonymous
  // demo visitor's document never gets rasterized server-side. Kicked off
  // by startRealUpload as soon as the file is known - not gated on
  // phase === 'done' - so rendering happens WHILE the extraction poll is
  // still in flight, and the real thumbnail is normally already sitting in
  // state by the time the result panel needs it (see thumbnailGenerationRef
  // below for how a second drop invalidates an in-flight render). Best
  // effort: the decorative thumbnail "stack" placeholder stays up as a
  // fallback while it's loading, and if rendering fails outright (e.g. a
  // corrupt PDF).
  const thumbnailGenerationRef = useRef(0);
  function startThumbnailRender(file) {
    const generation = ++thumbnailGenerationRef.current;
    setThumbnailUrl(null);
    renderFirstPageThumbnail(file).then((dataUrl) => {
      if (thumbnailGenerationRef.current === generation) setThumbnailUrl(dataUrl);
    });
  }

  // Sizes the thumbnail's own box once the real image's natural dimensions
  // are known - a plain CSS percentage max-height/max-width can't do this
  // (a scanned page's aspect ratio varies per upload, and the wrapper's
  // only sizeable content is this same image, so there's no ancestor with
  // a definite size for a percentage to resolve against without either
  // stretching the wrapper to the full placeholder - reintroducing the
  // letterboxing this is meant to fix - or collapsing it to nothing).
  // Recomputes on window resize too, since the placeholder's own size
  // changes at the lg breakpoint (grid-cols-1 vs lg:grid-cols-12 above).
  useEffect(() => {
    if (!thumbnailUrl) {
      setThumbBoxSize(null);
      return;
    }
    // The thumbnail itself can finish rendering (see startThumbnailRender)
    // well before the DONE panel - and thumbContainerRef's div - exist at
    // all, since generation now starts on drop instead of waiting for
    // phase === 'done'. Without this, recompute's own container-null guard
    // below swallows that first attempt and nothing re-triggers it once the
    // container finally mounts, leaving thumbBoxSize stuck at null forever
    // and the placeholder stack showing in its place. Re-runs (and this
    // time finds the mounted container) once phase actually reaches 'done'.
    if (phase !== 'done') return;
    let cancelled = false;
    let naturalRatio = null;

    function recompute() {
      const container = thumbContainerRef.current;
      if (!container || !naturalRatio) return;
      const maxWidth = container.clientWidth * 0.75;
      const maxHeight = container.clientHeight * 0.75;
      let width = maxWidth;
      let height = width / naturalRatio;
      if (height > maxHeight) {
        height = maxHeight;
        width = height * naturalRatio;
      }
      setThumbBoxSize({ width, height });
    }

    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      naturalRatio = img.naturalWidth / img.naturalHeight;
      recompute();
    };
    img.src = thumbnailUrl;

    window.addEventListener('resize', recompute);
    return () => {
      cancelled = true;
      window.removeEventListener('resize', recompute);
    };
  }, [thumbnailUrl, phase]);

  // Makes the "result" (a completed trial scan) and "signup" panels real,
  // back-button-navigable history entries via a #hash anchor - the syncing
  // effect right below is what reacts to it, including the browser's own
  // back/forward buttons. SCANNING and ERROR stay local-only (never
  // pushed) since they're transient - there's nothing useful to land on if
  // the visitor backs into a spinner. Skips the navigation entirely when
  // the URL already matches, so re-entering the same view doesn't pile up
  // duplicate entries the visitor would have to hit "back" through more
  // than once. preventScrollReset is what actually matters here - without
  // it, root.jsx's <ScrollRestoration/> resets the page to the top on
  // every one of these pushes, fighting the sync effect's own centered
  // scrollIntoView below.
  function pushHash(hash) {
    if (location.hash === hash) return;
    navigate(`${location.pathname}${location.search}${hash}`, { preventScrollReset: true });
  }

  // The reverse direction of pushHash() above - runs on every location
  // change, including back/forward, and brings the box's own panel (and,
  // for #preview/#start-trial/#create-account, its centered scroll
  // position) in line with the URL.
  useEffect(() => {
    const hash = location.hash;

    if (hash === HASH_START_TRIAL || hash === HASH_CREATE_ACCOUNT) {
      setSignupReason(hash === HASH_START_TRIAL ? 'voluntary' : 'forced');
      setShowFreeAccountBox(true);
      setFocusMode(false);
      requestAnimationFrame(() => {
        trialBoxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      return;
    }

    if (hash === HASH_PREVIEW) {
      if (!result) {
        // Nothing to show - e.g. a direct or refreshed load of a
        // #preview URL. The demo result only ever lives in memory, so fix
        // the URL up to match reality instead of leaving the idle panel
        // showing underneath a URL that claims otherwise.
        navigate(`${location.pathname}${location.search}`, { replace: true, preventScrollReset: true });
        return;
      }
      setShowFreeAccountBox(false);
      setFocusMode(false);
      setPhase('done');
      requestAnimationFrame(() => {
        trialBoxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      return;
    }

    // No matching hash - idle. If the visitor used the browser's own
    // back/forward while a scan or Show More reprocess was still in
    // flight (neither is itself a pushed entry, see pushHash above),
    // abandon it rather than let a stale timer resurface a result after
    // they've already navigated away from it.
    setShowFreeAccountBox(false);
    setFocusMode(false);
    setPhase((p) => {
      if (p === 'scanning') scanTimers.current.forEach(clearTimeout);
      return p === 'error' ? p : 'idle';
    });
    // Reacts to navigation (location.hash) only - result is read for its
    // current value, not to re-run this on every scan.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  // Shared by every place a "waiting on a result" panel (the initial scan
  // or a Show More reprocess - see startShowMore below) concludes. Modal/
  // focus mode is only ever on while the SCANNING panel is showing, so
  // leaving it always means turning focus mode back off. `recenter` is only
  // passed by the ERROR paths (there's no #hash for that state, so nothing
  // else re-centers the box once it's inline again); the success/signup
  // paths call pushHash right after this instead, and it's the hash-sync
  // effect above - not here - that does their recentering, since it has to
  // run after the hash has actually changed regardless of how it changed
  // (browser back/forward included).
  function exitScanningPanel({ recenter } = {}) {
    setFocusMode(false);
    if (recenter) {
      // Height and scroll position both just changed - the box is back in
      // normal flow, not necessarily centered in the viewport anymore.
      requestAnimationFrame(() => {
        trialBoxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }

  function runScan(data, src) {
    scanTimers.current.forEach(clearTimeout);
    scanTimers.current = [];

    // No dropped file behind the scripted sample - invalidate any real
    // upload's in-flight thumbnail render so it can't land after this.
    droppedFileRef.current = null;
    thumbnailGenerationRef.current += 1;
    setThumbnailUrl(null);

    setSource(src);
    setPhase('scanning');
    setIsReprocessing(false);
    setFocusMode(true);
    setResult(null);
    setTechText('');
    setCustRevealed(false);
    setCopiedTech(false);
    setCopiedCust(false);

    scanTimers.current.push(
      setTimeout(() => {
        setPhase('done');
        setResult(data);
        exitScanningPanel();
        pushHash(HASH_PREVIEW);

        let i = 0;
        const fullText = data.job.tech;
        const step = () => {
          i = Math.min(i + TYPEWRITER_STEP_CHARS, fullText.length);
          setTechText(fullText.slice(0, i));
          if (i < fullText.length) {
            scanTimers.current.push(setTimeout(step, TYPEWRITER_STEP_MS));
          }
        };
        step();

        scanTimers.current.push(setTimeout(() => setCustRevealed(true), CUSTOMER_REVEAL_DELAY_MS));
      }, SCAN_DELAY_MS)
    );
  }

  // Reveals a real extraction result through the exact same typewriter/
  // reveal beats runScan uses for the simulated sample, so the two are
  // visually indistinguishable once the content starts appearing.
  function revealResult(shaped) {
    setPhase('done');
    setResult(shaped);
    exitScanningPanel();
    pushHash(HASH_PREVIEW);

    let i = 0;
    const fullText = shaped.job.tech;
    const step = () => {
      i = Math.min(i + TYPEWRITER_STEP_CHARS, fullText.length);
      setTechText(fullText.slice(0, i));
      if (i < fullText.length) {
        scanTimers.current.push(setTimeout(step, TYPEWRITER_STEP_MS));
      }
    };
    step();

    scanTimers.current.push(setTimeout(() => setCustRevealed(true), CUSTOMER_REVEAL_DELAY_MS));
  }

  // Reshapes services/repairOrders' resource (via the tourguide passthrough
  // - see allowlist.js) into the {ro, vehicle, jobCount, job} shape the
  // existing render below already expects, so it needs no branching of its
  // own between a real result and the simulated one. uploadId rides along
  // for the "Show More" button, which only uses it to know a real upload -
  // with a real file behind it in droppedFileRef - exists to preview
  // further; see startShowMore below for the actual full-preview re-upload.
  async function loadRepairOrderResult(repairOrderId, uploadId) {
    const res = await fetch(`/tour/api/repairOrder/${encodeURIComponent(repairOrderId)}`);
    if (!res.ok) throw new Error(await readErrorMessage(res, 'Could not load the extraction result.'));
    const data = await res.json();

    // Not every job carries technician notes (some ops are parts-only, or the
    // scan just didn't have any for that line) - prefer the first one that
    // actually has some to preview, since an empty Workshop Notes box is a
    // poor first impression of the product. Falls back to the first job
    // outright if none of them do.
    const previewIndex = data.operations?.findIndex((op) => (op.editedTechNotes || op.rawTechNotes || '').trim());
    const previewOp = (previewIndex >= 0 ? data.operations[previewIndex] : data.operations?.[0]);
    const cust = previewOp?.customerSummary
      ? [previewOp.customerSummary.cause, previewOp.customerSummary.fix].filter(Boolean).join(' ')
      : '';

    revealResult({
      // No fallback to repairOrderId here - that's WriteUp's own internal
      // id, not a real RO number, and showing it as one would be
      // misleading when the scan genuinely didn't have one. `result?.ro ??
      // '—'` in the render below already handles the null case.
      ro: data.header?.roNumber || null,
      vehicle: data.header?.vehicleModel || '—',
      jobCount: data.operations?.length || 0,
      jobIndex: previewIndex >= 0 ? previewIndex : 0,
      job: {
        concern: previewOp?.statedProblem || '—',
        tech: previewOp?.editedTechNotes || previewOp?.rawTechNotes || '',
        cust: cust || 'No customer-facing summary was generated for this document.'
      },
      uploadId
    });
  }

  // Polls the same way UploadScreen.jsx does, just at a fixed interval - a
  // simpler regime is fine for a landing-page card rather than the full
  // screen's ramping schedule. INTERRUPTED (an exact re-upload of a file
  // already processed for this anonymous visitor) is treated exactly like
  // DONE rather than shown a confirmation - see docs/tourGuide.md point 4,
  // "Duplicate RO errors are silently treated as do not re-import". `onDone`
  // is what differs between the initial scan (populate the trial box's own
  // preview, see loadRepairOrderResult) and a Show More reprocess (navigate
  // straight to the full order - see startShowMore) - both stay on this
  // same in-box polling loop either way, never the separate wait screen.
  async function pollForResult(uploadId, onDone) {
    for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
      const res = await fetch(`/tour/api/file/${encodeURIComponent(uploadId)}`);
      if (!res.ok) throw new Error('Could not check on this upload.');
      const data = await res.json();

      if (data.status === 'DONE' || data.status === 'INTERRUPTED') {
        await onDone(data.repairOrderId);
        return;
      }
      if (data.status === 'ERROR') {
        throw new Error(data.error?.message || 'Something went wrong while processing this document.');
      }
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    }
    throw new Error('This took longer than expected.');
  }

  // The signed-in hand-off out of this module and into the product one.
  //
  // Two things make this a real upload here rather than a navigation that
  // carries the file:
  //
  // - The product is a different bundle now, so /app/uploading/<id> is a
  //   document load. React Router state (which is history.state, and does
  //   structured-clone a File) does not survive one, so the file has to be
  //   POSTed from this page.
  // - tourguide's getUploadToken deliberately bows out for a signed-in
  //   session and answers `{isAnonymousUser: false}` with NO id - it mints
  //   ids on behalf of anonymous visitors only. The previous code read
  //   `tokenData.id` from that answer regardless and navigated to
  //   `/app/uploading/undefined`. A signed-in visitor can ask decipher for a
  //   token directly, which is what this does.
  //
  // The POST is awaited before navigating: it responds 202 as soon as the
  // file is saved and the uploads row exists, and UploadScreen.jsx only
  // POSTs a file itself when it has one in memory - arriving there before
  // the row existed would leave it polling an upload nobody ever sent.
  async function handOffToSignedInFlow(file) {
    const tokenRes = await fetch('/api/files/getUploadToken', { method: 'POST' });
    if (!tokenRes.ok) throw new Error('Could not start this upload.');
    const { id } = await tokenRes.json();

    const formData = new FormData();
    formData.append('file', file);
    formData.append('uploadId', id);
    const uploadRes = await fetch('/api/files', { method: 'POST', body: formData });
    if (!uploadRes.ok) throw new Error('That file could not be uploaded.');

    navigateToModule(`/app/uploading/${encodeURIComponent(id)}`);
  }

  // Loaded and run at most once per visit, not up front - most visitors
  // never drop a real file (many just click "sample"), so there's no reason
  // to pay for this on every landing-page load. Cached in visitorIdRef so a
  // later "Show More" full-preview upload (see startShowMore) doesn't redo
  // the same detection work for the same device.
  async function getVisitorId() {
    if (visitorIdRef.current) return visitorIdRef.current;
    const { default: FingerprintJS } = await import('@fingerprintjs/fingerprintjs');
    const agent = await FingerprintJS.load();
    const { visitorId } = await agent.get();
    visitorIdRef.current = visitorId;
    return visitorId;
  }

  // The real counterpart to runScan, for an actual dropped/picked file -
  // see services/tourguide's POST /tour/api/files/getUploadToken and POST
  // /tour/api/files, and CLAUDE.md's tour-guide section for the full
  // choreography this mirrors from the frontend side.
  async function startRealUpload(file, via) {
    // 'drop' vs 'picker' - the two ways in, worth telling apart because the
    // dropzone and the button are separate design decisions.
    trackDemoUploadStarted(via);

    scanTimers.current.forEach(clearTimeout);
    scanTimers.current = [];

    droppedFileRef.current = file;
    startThumbnailRender(file);

    setSource('upload');
    setPhase('scanning');
    setIsReprocessing(false);
    setFocusMode(true);
    setResult(null);
    setTechText('');
    setCustRevealed(false);
    setCopiedTech(false);
    setCopiedCust(false);
    setUploadError(null);

    try {
      if (!demoKeyRef.current) {
        demoKeyRef.current = generateDemoKey();
      }
      const tokenRes = await fetch('/tour/api/files/getUploadToken', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ encryptionKey: demoKeyRef.current })
      });
      if (!tokenRes.ok) throw new Error(await readErrorMessage(tokenRes, 'Could not start this upload.'));
      const tokenData = await tokenRes.json();

      if (!tokenData.isAnonymousUser) {
        // A real signed-in visitor dropped a file here - hand off to the
        // normal signed-in flow instead of the anonymous tour-guide path.
        await handOffToSignedInFlow(file);
        return;
      }

      const uploadId = tokenData.id;

      const visitorId = await getVisitorId();

      const formData = new FormData();
      formData.append('file', file);
      formData.append('uploadId', uploadId);
      formData.append('fingerprint', visitorId);
      formData.append('kind', 'demo');

      const uploadRes = await fetch('/tour/api/files', { method: 'POST', body: formData });
      if (uploadRes.status === 409 || uploadRes.status === 402) {
        const payload = await uploadRes.json().catch(() => ({}));
        if (payload.errorType === 'TRIAL_QUOTA_EXCEEDED' || uploadRes.status === 402) {
          // In place of routing away to /subscribe's placeholder splash -
          // the trial box's own contents swap to the free-account box (see
          // freeSignup/FreeAccountBox.jsx) right here instead, so hitting
          // the demo quota doesn't lose the visitor's place on the page.
          // Not a failure - the trial cap is a product decision, and it is
          // the outcome most worth being able to count separately.
          trackDemoUploadResult('quota');
          setPhase('idle');
          exitScanningPanel();
          pushHash(HASH_CREATE_ACCOUNT);
          return;
        }
      }
      if (!uploadRes.ok) throw new Error(await readErrorMessage(uploadRes, 'That file could not be uploaded.'));

      await pollForResult(uploadId, (repairOrderId) => loadRepairOrderResult(repairOrderId, uploadId));
      trackDemoUploadResult('done');
    } catch (err) {
      trackDemoUploadResult('error');
      setPhase('error');
      setUploadError(err.message || 'Something went wrong. Please try again.');
      exitScanningPanel({ recenter: true });
    }
  }

  // "Show More" used to ask decipher to reprocess the same uploadId at a
  // fuller prompt quality, re-reading the file it had archived. That file is
  // no longer kept for an anonymous visitor (see CLAUDE.md's tour-guide
  // section), so there is nothing left there to reread - this re-sends the
  // same File from droppedFileRef instead, under a FRESH upload id minted
  // the same way startRealUpload's own does (decipher's uploads table has no
  // upsert path and refuses a second insert under an id already in use).
  // `kind: 'full'` on the upload tells services/tourguide to draw a
  // trial_ro_extraction credit (not demo_ro_extraction) and ask decipher for
  // the fuller 'ro_extraction_simple' prompt quality instead of
  // 'ro_extraction_demo' - see that service's POST /tour/api/files.
  //
  // Metering may refuse this the same way startRealUpload's own upload call
  // can (no demo/trial plan term, or the trial_ro_extraction bucket's
  // already spent) - checked here, before navigating away, so a 402 keeps
  // the visitor on this page and swaps in the free-account box the same way
  // startRealUpload does, instead of bouncing them to UploadScreen.jsx's own
  // "Out of Credits" error screen. Reuses the same SCANNING panel/modal/
  // polling as the initial scan while the upload is in flight
  // (isReprocessing just swaps the title) - deliberately never hands off to
  // the separate /tour/uploading wait screen; the trial box stays the one
  // place this whole demo happens, right up until the full order is
  // actually ready, at which point there's nowhere left for this page to
  // show it and navigating away is the point.
  async function startShowMore(uploadId) {
    const file = droppedFileRef.current;
    if (!uploadId || !file) return;
    // Asked for, not yet seen - full-demo-viewed is fired by the screen that
    // actually renders it (routes/tourLayout.jsx), so the drop-off between the
    // two is visible.
    trackFullDemoStarted();
    setIsReprocessing(true);
    setPhase('scanning');
    setFocusMode(true);
    try {
      const tokenRes = await fetch('/tour/api/files/getUploadToken', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ encryptionKey: demoKeyRef.current })
      });
      if (!tokenRes.ok) throw new Error(await readErrorMessage(tokenRes, 'Could not start the full preview.'));
      const { id: fullUploadId } = await tokenRes.json();

      const visitorId = await getVisitorId();

      const formData = new FormData();
      formData.append('file', file);
      formData.append('uploadId', fullUploadId);
      formData.append('fingerprint', visitorId);
      formData.append('kind', 'full');

      const res = await fetch('/tour/api/files', { method: 'POST', body: formData });
      if (res.status === 402) {
        setIsReprocessing(false);
        setPhase('done');
        exitScanningPanel();
        pushHash(HASH_CREATE_ACCOUNT);
        return;
      }
      if (!res.ok) throw new Error(await readErrorMessage(res, 'Could not start the full preview.'));

      await pollForResult(fullUploadId, (repairOrderId) => {
        // /tour/* belongs to the product module - a document navigation.
        navigateToModule(`/tour/ro/${encodeURIComponent(repairOrderId)}`);
      });
    } catch (err) {
      setIsReprocessing(false);
      setPhase('error');
      setUploadError(err.message || 'Something went wrong. Please try again.');
      exitScanningPanel({ recenter: true });
    }
  }

  function copyText(text, setCopied) {
    navigator.clipboard?.writeText(text).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) startRealUpload(e.dataTransfer.files[0], 'drop');
  }

  function handleFileChange(e) {
    if (e.target.files?.[0]) startRealUpload(e.target.files[0], 'picker');
  }

  function showComingSoon(e) {
    e.preventDefault();
    clearTimeout(comingSoonTimer.current);
    setComingSoon(true);
    comingSoonTimer.current = setTimeout(() => setComingSoon(false), COMING_SOON_MS);
  }

  // Drives the box's own header (see the "Interactive demo" section below).
  // Differentiates a forced signup (no demo credit left to spend) from a
  // voluntary one (visitor clicked "Get started for FREE" or "Show More"
  // while credit was still available) and a reprocess (Show More) from the
  // initial scan.
  const boxTitle = showFreeAccountBox
    ? signupReason === 'voluntary'
      ? 'Create a free account to get started'
      : 'Create a free account to continue.'
    : phase === 'scanning'
    ? isReprocessing
      ? 'Now reading the rest of the order…'
      : 'Reading your repair order…'
    : phase === 'error'
    ? 'Oops!'
    : phase === 'done'
    ? 'Your digital repair order, ready in seconds'
    : 'Try it out';

  // Gives the #preview/#start-trial/#create-account anchors above a real
  // element to point at, matching whichever one pushHash last navigated to.
  const boxId = showFreeAccountBox
    ? signupReason === 'voluntary'
      ? 'start-trial'
      : 'create-account'
    : phase === 'done'
    ? 'preview'
    : undefined;

  return (
    <div className="bg-ink-950 text-paper font-sans antialiased min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-ink-950/85 backdrop-blur-md border-b border-ink-800">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/icon.svg" alt="" className="w-8 h-8 rounded-lg" />
            <span className="font-display font-bold text-lg tracking-tight">
              <span className="text-paper">Write</span>
              <span className="text-brand">Up</span>
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm">
            <span className="relative text-paper font-medium pb-1 after:content-[''] after:absolute after:left-0 after:right-0 after:-bottom-0.5 after:h-0.5 after:bg-brand">
              For Service Advisors
            </span>
            <div className="relative">
              <a href="#dealerships" onClick={showComingSoon} className="text-ink-400 hover:text-paper transition-colors pb-1">
                For Dealerships
              </a>
              <div
                className={`pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-3 whitespace-nowrap px-3 py-1.5 rounded-lg bg-ink-800 border border-ink-700 text-paper text-xs font-medium shadow-lg transition-all duration-200 ${
                  comingSoon ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
                }`}
              >
                Coming soon!
                <span className="absolute left-1/2 -translate-x-1/2 -top-1 w-2 h-2 bg-ink-800 border-l border-t border-ink-700 rotate-45" />
              </div>
            </div>
          </nav>
          {/* Login/Sign Up is what this renders until told otherwise, and that
              is the right default rather than a placeholder: it's what the
              PRERENDERED html carries (see react-router.config.js), it's
              correct for the overwhelming majority of visitors to a public
              marketing page, and it means the primary call to action is in
              the first paint instead of waiting on a round trip. A signed-in
              visitor is the exception, and swapping the corner once
              /auth/status answers is the whole cost of serving them. */}
          <div className="flex items-center gap-2 sm:gap-3">
            {signedIn ? (
              <>
                <span className="hidden sm:inline text-sm text-ink-400">Welcome Back!</span>
                <ModuleLink
                  to="/app"
                  className="px-4 py-2 rounded-lg bg-brand text-ink-900 font-display font-semibold text-sm hover:bg-brand-600 transition-colors"
                >
                  Go to App
                </ModuleLink>
              </>
            ) : (
              <>
                <ModuleLink to="/sign-in" className="px-3 py-2 rounded-lg text-sm font-medium text-ink-300 hover:text-paper transition-colors">
                  Login
                </ModuleLink>
                <ModuleLink
                  to="/sign-up"
                  onClick={() => trackSignupCta(SIGNUP_SOURCE.LANDING_HEADER)}
                  className="px-4 py-2 rounded-lg bg-brand text-ink-900 font-display font-semibold text-sm hover:bg-brand-600 transition-colors"
                >
                  Sign Up
                </ModuleLink>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Focus-mode backdrop */}
      {focusMode && (
        <div
          onClick={() => setFocusMode(false)}
          className="fixed inset-0 z-[9998] bg-ink-950/70 backdrop-blur-sm"
        />
      )}

      {/* Hero */}
      <section className="relative overflow-hidden pt-14 pb-6">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[640px] h-[320px] bg-brand/10 rounded-full blur-[120px] pointer-events-none" />
        <div className="max-w-5xl mx-auto px-5 sm:px-8 relative">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h1 className="font-display font-bold text-3xl sm:text-4xl leading-tight tracking-tight text-paper mb-4 text-balance">
              Your Handwriting Expert for the Workshop.
              <span className="block mt-2 text-brand font-normal">
                Even learns your tech's{' '}
                <span className="font-['Reenie_Beanie'] -tracking-[1px] -rotate-3 -skew-x-[10deg] inline-block text-5xl sm:text-6xl">
                  scribble.
                </span>
              </span>
            </h1>
            <p className="text-ink-300 text-base leading-relaxed max-w-xl mx-auto">
              Snap a repair order with your phone, or drop a scanned file below. With WriteUp,
              even the hardest to read technician notes are ready to be copy-pasted into your DMS in seconds.
            </p>
          </div>
        </div>
      </section>

      {/* Interactive demo */}
      <section id="try" className="pb-16">
        {/* In focus mode this wrapper leaves the document flow entirely and
            becomes a fixed, flex-centered dialog frame - the trial/free-
            account box inside it is centered by flexbox on every render, so
            it stays centered on screen even as its own height changes (a
            job's concern wrapping to two lines, the free-account box
            swapping in at a different height, etc.) rather than only being
            centered once, at the moment focus mode turned on. */}
        <div className={focusMode ? 'fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6' : 'max-w-5xl mx-auto px-5 sm:px-8'}>
          <div
            ref={trialBoxRef}
            id={boxId}
            className={`bg-ink-900 border border-ink-700 rounded-2xl shadow-2xl shadow-black/40 p-4 sm:p-6 ${focusMode ? 'w-full max-w-5xl max-h-full overflow-y-auto' : ''}`}
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 mb-4 border-b border-ink-800">
              <div className="flex items-center gap-2.5">
                {!showFreeAccountBox && <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />}
                <h2 className="font-display font-bold text-2xl text-paper text-balance">
                  {boxTitle}
                </h2>
              </div>
              {focusMode && (
                <button
                  onClick={() => setFocusMode(false)}
                  aria-label="Close"
                  className="shrink-0 p-1 text-brand/70 hover:text-brand transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Hands off into the same wizard /sign-up renders, opened on
                its second step with these two fields already carried over
                (see freeSignup/SignUpWizard.jsx) - one signup flow, two
                entry points, rather than a landing-page-only variant. */}
            {showFreeAccountBox && (
              <FreeAccountBox
                onContinue={({ name, email }) => {
                  // signupReason is already tracked on this page: 'voluntary'
                  // is someone who chose to start a trial, 'forced' is someone
                  // the demo quota pushed here. Same button, two very
                  // different intents, and the conversion rates will differ.
                  trackSignupCta(SIGNUP_SOURCE.LANDING_TRIAL_BOX, { reason: signupReason || 'voluntary' });
                  // The wizard is in another module now, so router state
                  // cannot carry these two fields - see @ui/signupHandoff.js
                  // for why it is sessionStorage and not a query parameter.
                  stashSignupPrefill({ name, email });
                  navigateToModule('/sign-up');
                }}
              />
            )}

            {/* IDLE: dropzone + how-it-works steps */}
            {!showFreeAccountBox && phase === 'idle' && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-stretch">
              {/* Upload / preview */}
              <div className="lg:col-span-5">
                <div
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragActive(true);
                  }}
                  onDragLeave={() => setDragActive(false)}
                  onDrop={handleDrop}
                  className={`relative cursor-pointer border-2 border-dashed rounded-xl h-full min-h-[300px] flex flex-col items-center justify-center text-center p-6 transition-colors bg-ink-950/40 ${
                    dragActive ? 'border-brand' : 'border-ink-700 hover:border-brand/60'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,.pdf"
                    className="hidden"
                    onChange={handleFileChange}
                  />

                  <div className="space-y-3">
                    <div className="w-12 h-12 mx-auto rounded-full bg-brand/10 border border-brand/25 flex items-center justify-center text-brand">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                    </div>
                    <p className="text-sm font-medium text-paper">Drop a repair order here</p>
                    <p className="text-xs text-ink-400">PDF, JPEG, PNG</p>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        trackSampleOrderViewed('landing');
                        runScan(SAMPLE, 'sample');
                      }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border border-ink-700 transition ${
                        source === 'sample' ? 'bg-ink-700 text-paper' : 'bg-ink-800 hover:bg-ink-700 text-ink-200'
                      }`}
                    >
                      Or try a sample order
                    </button>
                  </div>
                </div>
              </div>

              {/* Output */}
              <div className="lg:col-span-7">
                <div className="h-full flex flex-col justify-center gap-5">
                  <h3 className="font-display font-bold text-lg text-paper text-center mb-1">From paper copy to happy customer</h3>
                  {[
                    ['1', 'Paper copy', 'Use our mobile app to scan a repair order (or upload a scanned file).'],
                    ['2', 'Digital tech write-up', "Your technician's handwriting is ready to be copy-pasted in seconds."],
                    ['3', 'Happy customer', 'Explain to your customer what was done — using everyday language.']
                  ].map(([num, title, body]) => (
                    <div key={num} className="flex items-start gap-4">
                      <span className="shrink-0 w-9 h-9 rounded-full bg-brand/10 border border-brand/25 text-brand font-display font-bold text-sm flex items-center justify-center">
                        {num}
                      </span>
                      <div>
                        <h4 className="font-display font-semibold text-paper mb-1">{title}</h4>
                        <p className="text-sm text-ink-400 leading-relaxed">{body}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            )}

            {!showFreeAccountBox && phase === 'idle' && (
            <div className="mt-5 pt-4 border-t border-ink-800 flex items-center gap-2 text-xs text-ink-500">
              <svg className="w-3.5 h-3.5 text-ink-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span>
                Encryption is active. By uploading a document, you consent to the{' '}
                <Link to="/demo-terms" className="text-ink-400 underline hover:text-ink-300">
                  Demonstration Terms of Use
                </Link>
                .
              </span>
            </div>
            )}

            {/* SCANNING: the box's entire body becomes a progress animation
                while a result (initial scan or Show More reprocess) is
                pending - see startShowMore/exitScanningPanel above. */}
            {!showFreeAccountBox && phase === 'scanning' && (
            <div className="py-10">
              <div className="max-w-sm mx-auto flex flex-col items-center text-center gap-5">
                <div className="relative w-20 h-20 rounded-2xl bg-ink-950/60 border border-ink-800 flex items-center justify-center overflow-hidden">
                  <div className="absolute inset-3 flex flex-col justify-center gap-1.5 opacity-60">
                    <div className="h-1 rounded-full bg-ink-700 w-full" />
                    <div className="h-1 rounded-full bg-ink-700 w-4/5" />
                    <div className="h-1 rounded-full bg-ink-700 w-full" />
                    <div className="h-1 rounded-full bg-ink-700 w-3/5" />
                    <div className="h-1 rounded-full bg-ink-700 w-full" />
                  </div>
                  <div className="scan-line absolute left-2 right-2 h-0.5 bg-brand shadow-[0_0_16px_2px_rgba(242,169,60,0.6)]" />
                </div>
                <div className="w-8 h-8 rounded-full border-2 border-ink-700 border-t-brand animate-spin" />
              </div>
            </div>
            )}

            {/* ERROR: only action is "Try another file" */}
            {!showFreeAccountBox && phase === 'error' && (
            <div className="py-10">
              <div className="max-w-sm mx-auto flex flex-col items-center text-center gap-4">
                <div className="w-14 h-14 rounded-full bg-rose-500/10 border border-rose-500/25 flex items-center justify-center text-rose-400">
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                  </svg>
                </div>
                <p className="text-[16px] font-medium text-paper">{uploadError || 'Something went wrong.'}</p>
                <p className="text-[14px] text-ink-500 leading-relaxed">The scan might be too blurry, or not a repair order at all.</p>
                <button
                  onClick={() => {
                    setPhase('idle');
                    setSource(null);
                  }}
                  className="px-4 py-2.5 rounded-lg bg-brand text-ink-900 font-display font-semibold text-sm hover:bg-brand-600 transition-colors"
                >
                  Try another file
                </button>
              </div>
            </div>
            )}

            {/* DONE: typewriter starts the moment this panel is shown */}
            {!showFreeAccountBox && phase === 'done' && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-stretch">
              {/* Imported repair order - a real first-page thumbnail once
                  it's rendered (see startThumbnailRender above), a static
                  stack placeholder otherwise (sample scans have no real page to
                  show). Not a dropzone anymore: the trial box already has
                  its one scan, and clicking here shouldn't reopen the file
                  picker (a new scan starts from the idle panel - see the
                  #hash history section above for how a visitor gets back
                  there). */}
              <div className="lg:col-span-5">
                <div ref={thumbContainerRef} className="h-full min-h-[300px] rounded-xl border border-ink-800 bg-ink-950/40 flex items-center justify-center p-6 overflow-hidden">
                  {thumbnailUrl && thumbBoxSize ? (
                    // Sized in JS (see the effect above), not CSS - a
                    // scanned page's own aspect ratio varies per upload, so
                    // the box has to hug whatever that turns out to be
                    // rather than assume one. The two cards behind it reuse
                    // that same image, darkened, rather than blank - "page
                    // 2 and 3" of the same document, not a generic stack
                    // icon; overflow-hidden above keeps their rotation from
                    // spilling past the placeholder.
                    <div className="relative" style={{ width: thumbBoxSize.width, height: thumbBoxSize.height }}>
                      <img
                        src={thumbnailUrl}
                        aria-hidden="true"
                        className="absolute inset-0 w-full h-full object-cover rounded-lg border border-ink-700 translate-x-2.5 translate-y-2.5 rotate-2 brightness-[.35] saturate-50"
                      />
                      <img
                        src={thumbnailUrl}
                        aria-hidden="true"
                        className="absolute inset-0 w-full h-full object-cover rounded-lg border border-ink-700 translate-x-1.5 translate-y-1.5 rotate-1 brightness-[.55] saturate-50"
                      />
                      <img
                        src={thumbnailUrl}
                        alt="First page of the scanned repair order"
                        className="absolute inset-0 w-full h-full object-contain rounded-lg border border-ink-700 bg-ink-900"
                      />
                    </div>
                  ) : (
                    <div className="relative w-3/4 aspect-[3/4]">
                      <div className="absolute inset-0 translate-x-3 translate-y-3 rotate-6 rounded-lg bg-ink-800 border border-ink-700" />
                      <div className="absolute inset-0 translate-x-1.5 translate-y-1.5 rotate-3 rounded-lg bg-ink-800 border border-ink-700" />
                      <div className="absolute inset-0 rounded-lg bg-ink-900 border border-ink-700 flex flex-col justify-center gap-1.5 p-5">
                        <div className="h-1.5 rounded-full bg-ink-700 w-full" />
                        <div className="h-1.5 rounded-full bg-ink-700 w-4/5" />
                        <div className="h-1.5 rounded-full bg-ink-700 w-full" />
                        <div className="h-1.5 rounded-full bg-ink-700 w-3/5" />
                        <div className="h-1.5 rounded-full bg-ink-700 w-full" />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Output */}
              <div className="lg:col-span-7">
                <div className="flex flex-col gap-4">
                  <div className="bg-ink-950/60 border border-ink-800 rounded-lg px-4 py-3 flex items-start justify-between gap-3">
                    <div className="flex items-start gap-5">
                      <div>
                        <span className="block text-[10px] uppercase tracking-wider text-ink-500">RO #</span>
                        <span className="font-mono text-sm font-medium text-paper">{result?.ro ?? '—'}</span>
                      </div>
                      <div className="w-px self-stretch bg-ink-800" />
                      <div>
                        <span className="block text-[10px] uppercase tracking-wider text-ink-500">Vehicle</span>
                        <span className="text-sm font-medium text-paper">{result?.vehicle ?? '—'}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => startShowMore(result?.uploadId)}
                      disabled={!result?.uploadId}
                      title={result?.uploadId ? undefined : 'Drop a real file to see the full order'}
                      className="shrink-0 self-center px-3 py-1.5 rounded-lg text-xs font-medium bg-brand/10 hover:bg-brand/20 border border-brand/30 text-brand transition disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-brand/10"
                    >
                      Show More
                    </button>
                  </div>

                  <div className="bg-ink-950/60 border border-ink-800 rounded-lg px-4 py-3 flex flex-col gap-1.5">
                    <div className="flex items-center justify-between gap-3">
                      <h3 class="text-xs font-display font-semibold uppercase tracking-wider text-ink-300">Concern</h3>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => startShowMore(result?.uploadId)}
                          disabled={!result?.uploadId}
                          title="Previous job — view full order to browse jobs"
                          className="w-7 h-7 flex items-center justify-center rounded-md border border-ink-700 text-ink-300 hover:bg-ink-800 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent transition"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        <span className="text-xs font-mono text-ink-400 tabular-nums whitespace-nowrap">
                          Job {(result?.jobIndex ?? 0) + 1} of {result?.jobCount ?? '—'}
                        </span>
                        <button
                          onClick={() => startShowMore(result?.uploadId)}
                          disabled={!result?.uploadId}
                          title="Next job — view full order to browse jobs"
                          className="w-7 h-7 flex items-center justify-center rounded-md border border-ink-700 text-ink-300 hover:bg-ink-800 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent transition"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <span className="text-sm font-medium text-paper block">{result?.job.concern ?? '—'}</span>
                  </div>

                  <div className="bg-ink-950/40 border border-ink-800 rounded-xl p-4 flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xs font-display font-semibold uppercase tracking-wider text-ink-300">Workshop Notes</h3>
                      <button
                        onClick={() => copyText(techText, setCopiedTech)}
                        className="flex items-center gap-1 text-xs text-brand hover:text-brand-600 font-medium"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        <span>{copiedTech ? 'Copied!' : 'Copy'}</span>
                      </button>
                    </div>
                    <p ref={techOutRef} className="font-mono text-[13px] leading-relaxed text-ink-200 whitespace-pre-line h-[106px] overflow-hidden line-clamp-5">
                      {techText}
                      <span className="text-brand animate-pulse">▍</span>
                    </p>
                    {techClamped && (
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          startShowMore(result?.uploadId);
                        }}
                        className="mt-1.5 inline-block text-xs text-brand hover:text-brand-600 font-medium cursor-pointer"
                      >
                        Show more
                      </a>
                    )}
                  </div>

                  <div className="bg-ink-950/40 border border-ink-800 rounded-xl p-4 flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xs font-display font-semibold uppercase tracking-wider text-ink-300">Customer summary</h3>
                      <button
                        onClick={() => copyText(result?.job.cust ?? '', setCopiedCust)}
                        className="flex items-center gap-1 text-xs text-brand hover:text-brand-600 font-medium"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        <span>{copiedCust ? 'Copied!' : 'Copy'}</span>
                      </button>
                    </div>
                    <p className="text-sm leading-relaxed text-ink-200 h-[92px] overflow-hidden line-clamp-4">
                      {custRevealed ? result?.job.cust : ''}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            )}

            {!showFreeAccountBox && phase === 'done' && (
            <div className="mt-5 pt-4 border-t border-ink-800 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-xs text-ink-500">
                <svg className="w-3.5 h-3.5 text-ink-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v3.75m0 3.75h.007v.008H12v-.008zM21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>AI can make mistakes. Verify results.</span>
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-6">
              <p className="font-display font-semibold text-paper text-sm sm:text-base text-center sm:text-right">
                Have a stack of ROs to get through?
              </p>
              {custRevealed && (
                <button
                  onClick={() => {
                    trackSignupCta(SIGNUP_SOURCE.LANDING_FINAL_CTA);
                    pushHash(HASH_START_TRIAL);
                  }}
                  className="shrink-0 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-brand text-ink-900 font-display font-semibold text-base hover:bg-brand-600 transition-colors"
                >
                  Get started for FREE
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
              </div>
            </div>
            )}
          </div>
        </div>
      </section>

      <footer className="border-t border-ink-800 py-8">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-ink-500">
          <span>© 2026 WriteUp</span>
          {/* The /help, /terms and /privacy pages exist as routes but have no
              real content yet, so the footer points at a mailbox instead of
              linking to placeholders. */}
          <span>
            Need Help? E-Mail us at{' '}
            <a href="mailto:writeup-support@autopivot.com.au" className="hover:text-ink-300">
              writeup-support@autopivot.com.au
            </a>
            .
          </span>
        </div>
      </footer>
    </div>
  );
}
